import { convertTimeToUtc, convertUtcTimeToTimezone } from '../../../utils/dateTime';

// REMOVE unwanted keys
export const cleanCondition = (condition = {}) => {
    const { nextOperator, ...rest } = condition;
    return rest;
};

//  Handle IN clause parsing
export const parseInClauseValue = (condition = {}) => {
    const { attributeFilterClause, attributeFilterValue } = condition;

    // For IN clause, UI expects array in edit mode
    if (attributeFilterClause === "IN") {
        if (Array.isArray(attributeFilterValue)) return attributeFilterValue;
        if (typeof attributeFilterValue === "string") {
            return attributeFilterValue
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean);
        }
        if (attributeFilterValue === null || attributeFilterValue === undefined || attributeFilterValue === "") {
            return [];
        }
        return [attributeFilterValue];
    }

    return attributeFilterValue;
};

//  Reformat conditions for edit
export const reformatConditionsForEdit = (conditions = []) =>
    (conditions || []).map((c) => ({
        ...c,
        attributeFilterValue: parseInClauseValue(c),
    }));

//  Normalize condition value
export const normalizeValue = (condition) => {
    const { attributeFilterValue, attributeFilterType, attributeFilterClause } = condition;
    if (["storeIds", "geoTagIds", "customerGroups"].includes(attributeFilterType)) {
        if (Array.isArray(attributeFilterValue)) {
            const joinedValue = attributeFilterValue.map((s) =>
                typeof s === "object" ? s.id : s
            ).join(",");
            return attributeFilterClause === "IN" ? `${joinedValue},` : joinedValue;
        }
        if (typeof attributeFilterValue === "object") {
            return attributeFilterValue.id;
        }
    }

    if (!attributeFilterValue) return null;

    if (Array.isArray(attributeFilterValue)) {
        const joinedValue = attributeFilterValue
            .filter(Boolean)
            .map((v) =>
                typeof v === "object"
                    ? v[attributeFilterType] || v.name
                    : v
            ).join(",");
        return attributeFilterClause === "IN" ? `${joinedValue},` : joinedValue;
    }

    if (typeof attributeFilterValue === "object") {
        return attributeFilterValue[attributeFilterType] || attributeFilterValue.name;
    }

    return attributeFilterValue;
};

//  Format conditions for API
export const formatConditions = (conditions = []) => {
    return conditions
        .filter((condition) => hasConditionValue(condition.attributeFilterValue))
        .map(({ id, ...rest }) => ({
            ...rest,
            attributeFilterValue: normalizeValue(rest),
        }));
};

// Value checker
export const hasConditionValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === "string") {
        return value.trim() !== "";
    }

    if (typeof value === "object") {
        return Object.keys(value).length > 0;
    }

    return true;
};

export const normalizeThen = (thenObj) => {
    if (!thenObj?.subConditions?.length) return thenObj;

    let discount = thenObj.discount;
    let subConditions = thenObj.subConditions.map((block) => ({
        ...block,
        when: formatConditions(block.when),
    }));

    // Move discount from subCondition if when is empty
    subConditions = subConditions.filter((block) => {
        if (!block.when || block.when.length === 0) {
            if (block.then?.discount) {
                discount = block.then.discount;
            }
            return false;
        }
        return true;
    });

    return {
        ...thenObj,
        discount,
        subConditions: subConditions.length ? subConditions : null,
    };
};

const applyConverterToDays = (days, converter) =>
    days.map((day) => ({
        ...day,
        timeSlots: (day.timeSlots || []).map((slot) => ({
            start: converter(slot.start),
            end: converter(slot.end),
        })),
    }));

const parseFrequency = (frequency) => {
    if (!frequency || frequency === "none") return null;
    if (typeof frequency !== "string") return frequency;
    try { return JSON.parse(frequency); } catch { return null; }
};

const normalizeFrequency = (frequency, timezone) => {
    const parsed = parseFrequency(frequency);
    if (!parsed) return null;

    const days = Array.isArray(parsed) ? parsed : parsed?.daysOfWeek;
    if (Array.isArray(days) && days.length === 0) return null;
    if (!timezone || !Array.isArray(days)) return parsed;

    const converted = applyConverterToDays(days, (t) => convertTimeToUtc(t, timezone));
    return Array.isArray(parsed) ? converted : { ...parsed, daysOfWeek: converted };
};

export const denormalizeFrequency = (frequency, timezone) => {
    const parsed = parseFrequency(frequency);
    if (!parsed || !timezone) return frequency;

    const days = Array.isArray(parsed) ? parsed : parsed?.daysOfWeek;
    if (!Array.isArray(days)) return parsed;

    const converted = applyConverterToDays(days, (t) => convertUtcTimeToTimezone(t, timezone));
    return Array.isArray(parsed) ? converted : { ...parsed, daysOfWeek: converted };
};

export const normalizeForm = (data, timezone) => ({
    ...data,
    storeIds: (data.storeIds || []).map(s => typeof s === "object" ? s.id : s),
    geoTagIds: (data.geoTagIds || []).map(g => typeof g === "object" ? g.id ?? g : g),
    when: formatConditions(data.when),
    then: normalizeThen(data.then),
    frequency: normalizeFrequency(data.frequency, timezone),
});

export const createEmptySlot = () => ({ start: "", end: "" });

export const toHHMMSS = (v) => {
    if (!v) return "";
    if (/^\d{2}:\d{2}$/.test(v)) return `${v}:00`;
    return v;
};